import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MenusPagination = ({
  page,
  totalPages,
  onPageChange,
  maxVisible = 5,
  labels = {},
}) => {
  const { previous = "Previous", next = "Next", first = "First", last = "Last" } =
    labels;

  if (totalPages <= 1) {
    return null;
  }

  let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
  let endPage = startPage + maxVisible - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - maxVisible + 1);
  }

  const pages = [];

  if (startPage > 1) {
    pages.push(1);
    if (startPage > 2) {
      pages.push("...");
    }
  }

  for (let i = startPage; i <= endPage; i += 1) {
    pages.push(i);
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      pages.push("...");
    }
    pages.push(totalPages);
  }

  const baseButton =
    "relative inline-flex items-center border border-gray-300 bg-white px-3 py-2 text-sm font-medium";
  const disabledButton = "bg-gray-100 text-gray-400 cursor-not-allowed";
  const activeButton = "z-10 bg-primary-color border-primary-color text-white";
  const idleButton = "text-gray-500 hover:bg-gray-50";

  return (
    <nav
      className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
      aria-label="Pagination"
    >
      <button
        type="button"
        onClick={() => onPageChange(1)}
        disabled={page === 1}
        aria-label={first}
        className={`${baseButton} rounded-l-md ${
          page === 1 ? disabledButton : idleButton
        }`}
      >
        <span className="text-xs font-bold">«</span>
      </button>

      <button
        type="button"
        onClick={() => onPageChange(Math.max(page - 1, 1))}
        disabled={page === 1}
        aria-label={previous}
        className={`${baseButton} ${
          page === 1 ? disabledButton : idleButton
        }`}
      >
        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
      </button>

      {pages.map((pageNum, idx) =>
        pageNum === "..." ? (
          <span
            key={`ellipsis-${idx}`}
            className={`${baseButton} text-gray-700`}
          >
            ...
          </span>
        ) : (
          <button
            key={pageNum}
            type="button"
            onClick={() => onPageChange(pageNum)}
            className={`${baseButton} ${
              page === pageNum ? activeButton : idleButton
            }`}
          >
            {pageNum}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => onPageChange(Math.min(page + 1, totalPages))}
        disabled={page === totalPages}
        aria-label={next}
        className={`${baseButton} ${
          page === totalPages ? disabledButton : idleButton
        }`}
      >
        <ChevronRight className="h-5 w-5" aria-hidden="true" />
      </button>

      <button
        type="button"
        onClick={() => onPageChange(totalPages)}
        disabled={page === totalPages}
        aria-label={last}
        className={`${baseButton} rounded-r-md ${
          page === totalPages ? disabledButton : idleButton
        }`}
      >
        <span className="text-xs font-bold">»</span>
      </button>
    </nav>
  );
};

export default MenusPagination;
